import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ShoppingCartOutlined,
  Restaurant,
  Edit,
  CreditCard,
  QrCodeScanner,
  Payments,
  AccountBalance,
  ArrowBack,
  CheckCircle,
  PhoneIphone,
  Receipt,
} from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { tableService } from '../../services/tableService';
import { useNotification } from '../../contexts/NotificationContext';
import { useRestaurant } from '../../contexts/RestaurantContext';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';


const TableOrderCheckout = () => {
  const { orderId, uniqueString } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showSuccess, showError, showNotification } = useNotification();
  const { clearCart, addToCart } = useRestaurant(); // Added addToCart

  // State for customer information
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    email: '',
    specialInstructions: '',
  });

  // State for payment method
  const [paymentMethod, setPaymentMethod] = useState('counter');
  const [loading, setLoading] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderNumber, setOrderNumber] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [error, setError] = useState(null);
  const [cartLoading, setCartLoading] = useState(false);
  const [cartValues, setCartValues] = useState({
    cart_total: 0,
    tax: 0,
    tax_value: 0,
    service_charge: 0,
    service_charge_value: 0,
    total: 0,
  });
  const [tableOrderId, setTableOrderId] = useState()

  // Fetch table cart items
  const fetchTableCartItems = useCallback(async (tableId) => {
    if (!tableId) return { dinein: [], takeaway: [] };

    try {
      setCartLoading(true);
      const response = await tableService.getTableCartItems(tableId);
      console.log("Table Cart Items => ", response.data.data);

      const cartData = response.data.data;
      setTableOrderId(response.data.data.order_id)
      const dineInItems = cartData.dine_in?.items || [];
      const takeawayItems = cartData.takeaway?.items || [];

      setCartItems([...dineInItems, ...takeawayItems]);

      setCartValues({
        cart_total: cartData?.cart_total || 0,
        tax: cartData?.tax || 0,
        tax_value: cartData?.tax_value || 0,
        service_charge: cartData?.service_charge || 0,
        service_charge_value: cartData?.service_charge_value || 0,
        total: cartData?.final_total || 0,
      });

      return { dinein: dineInItems, takeaway: takeawayItems };
    } catch (error) {
      console.error("Error fetching cart items => ", error);
      return { dinein: [], takeaway: [] };
    } finally {
      setCartLoading(false);
    }
  }, []);

  // Fetch table data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const tableResponse = await tableService.getTableOrderPage(uniqueString);
        setTableData(tableResponse);

        // After getting table data, fetch cart items
        if (tableResponse?.table?.id) {
          await fetchTableCartItems(tableResponse.table.id);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Table not found or link is invalid');
      } finally {
        setLoading(false);
      }
    };

    if (uniqueString) {
      fetchData();
    }
  }, [uniqueString, fetchTableCartItems]);

  // Get data from location state or fetch from API
  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (location.state) {
        const items = location.state.cart || [];
        setCartItems(items);
        setOrderDetails(location.state);
        if (location.state.customerName) {
          setCustomerInfo(prev => ({ ...prev, name: location.state.customerName }));
        }
        if (location.state.customerPhone) {
          setCustomerInfo(prev => ({ ...prev, phone: location.state.customerPhone }));
        }
        if (location.state.specialInstructions) {
          setCustomerInfo(prev => ({ ...prev, specialInstructions: location.state.specialInstructions }));
        }
      } else if (orderId) {
        await fetchOrderById(orderId);
      }
    };

    fetchOrderDetails();
  }, [location.state, orderId]);

  const handleOrderStatusChange = async () => {
    try {
      const response = await tableService.tableOrderStatusChange(tableData?.table.id, tableOrderId);
      if (response) {
        navigate(`/table-order/${uniqueString}`)
      }
    }
    catch (error) {
      throw new Error(error)
    }
  }

  const fetchOrderById = async (id) => {
    try {
      setLoading(true);
      const response = await tableService.getOrderById(id);
      setOrderDetails(response.data);
      setCartItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching order:', error);
      showError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!customerInfo.name.trim()) {
      showNotification('Please enter customer name', 'error');
      return false;
    }
    if (!customerInfo.phone.trim()) {
      showNotification('Please enter phone number', 'error');
      return false;
    }
    const phoneRegex = /^[0-9]{8,15}$/;
    if (!phoneRegex.test(customerInfo.phone.replace(/\s/g, ''))) {
      showNotification('Please enter a valid phone number', 'error');
      return false;
    }
    return true;
  };

  const handlePlaceOrder = async () => {
    console.log("Cart Items => ", cartItems);
    // if (!validateForm()) return;
    setOpenConfirmDialog(true);
  };

  const confirmOrder = async () => {
    setOpenConfirmDialog(false);
    setLoading(true);

    try {
      const orderType = orderDetails?.orderType || 'dinein';
      let user_id = localStorage.getItem('user_id')
      let payload = {
        order_id: tableOrderId,
        // admin_id : user_id,
        customer_name: customerInfo.name,
        customer_phone: customerInfo.phone,
        customer_email: customerInfo.email || null,
        special_instructions: customerInfo.specialInstructions,
        payment_method: paymentMethod,
        order_type: orderType.toUpperCase(),
      };

      if (user_id !== "undefined") {
        payload["admin_id"] = user_id
      }

      console.log("Payload => ", payload);

      const response = await tableService.tableCheckout(payload);

      // if (response.data.order_number) {
      //   setOrderNumber(response.data.order_number);
      // }

      setOrderSuccess(true);
      showSuccess('Order placed successfully!');

      // Clear cart from context
      clearCart();

      // Auto redirect after 3 seconds
      // setTimeout(() => {
      //   navigate(`/order-success/${orderDetails?.tableOrderId || orderId}`, {
      //     state: {
      //       // orderNumber: response.data.order_number,
      //       customerName: customerInfo.name,
      //       orderType: orderDetails?.orderType || 'dinein',
      //     }
      //   });

      // }, 3000);

      navigate(`/table-order/${uniqueString}`)
    } catch (error) {
      console.error('Error placing order:', error);
      showError(error.response?.data?.message || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  const calculateSubtotal = () => {
    if (cartValues.cart_total > 0) {
      return cartValues.cart_total;
    }
    return cartItems.reduce((sum, item) => sum + ((item.price || item.base_price) * item.quantity), 0);
  };

  const calculateTax = (subtotal) => {
    if (cartValues.tax_value > 0) {
      return cartValues.tax_value;
    }
    const taxRate = orderDetails?.tax || 9;
    return subtotal * (taxRate / 100);
  };

  const calculateTotal = () => {
    if (cartValues.total > 0) {
      return cartValues.total;
    }
    const subtotal = calculateSubtotal();
    const tax = calculateTax(subtotal);
    return subtotal + tax;
  };

  const handlePhoneInputChange = (field, value) => {
    let phoneNumber_Length = value.target.countryValue.format.split('.').length - 1;
    // console.log("Phone Number Value => ", value)
    // setCountryNoLength(phoneNumber_Length)
    // setFormData(prev => (prev ? {
    //   ...prev,
    //   phoneNumber: value.target.value
    // } : null));

    setCustomerInfo(prev => (prev ? {
      ...prev,
      phone: value.target.value
    } : null))

  };


  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'paynow':
        return <QrCodeScanner sx={{ fontSize: 24 }} />;
      case 'grabpay':
        return <Payments sx={{ fontSize: 24 }} />;
      case 'upi':
        return <AccountBalance sx={{ fontSize: 24 }} />;
      case 'card':
        return <CreditCard sx={{ fontSize: 24 }} />;
      case 'counter':
        return <Receipt sx={{ fontSize: 24 }} />;
      default:
        return <PhoneIphone sx={{ fontSize: 24 }} />;
    }
  };

  const paymentMethods = [
    { value: 'paynow', label: 'PayNow', icon: 'paynow' },
    { value: 'grabpay', label: 'GrabPay', icon: 'grabpay' },
    { value: 'upi', label: 'UPI', icon: 'upi' },
    { value: 'card', label: 'Credit/Debit Card', icon: 'card' },
    { value: 'counter', label: 'Pay at Counter', icon: 'counter' },
  ];

  if (loading && !orderDetails && !tableData) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={60} sx={{ color: '#680000' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Paper sx={{ maxWidth: 500, width: '100%', p: 4, textAlign: 'center', borderRadius: '6px' }}>
          <Typography variant="h6" color="error" gutterBottom>
            Error
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {error}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(-1)}
            sx={{ bgcolor: '#680000', borderRadius: '6px' }}
          >
            Go Back
          </Button>
        </Paper>
      </Box>
    );
  }

  if (orderSuccess) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Paper sx={{ maxWidth: 500, width: '100%', p: 4, textAlign: 'center', borderRadius: '6px' }}>
          <CheckCircle sx={{ fontSize: 80, color: '#4caf50', mb: 2 }} />
          <Typography variant="h4" gutterBottom fontWeight={600} color="#680000">
            Order Placed!
          </Typography>
          <Typography variant="h6" gutterBottom>
            Order #{orderNumber}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Thank you for your order! Your order has been received and is being processed.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            You will be redirected shortly...
          </Typography>
          <CircularProgress size={30} sx={{ color: '#680000' }} />
        </Paper>
      </Box>
    );
  }

  const displayItems = cartItems || [];
  const currentOrderType = orderDetails?.orderType || 'dinein';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', pt: { xs: 2, md: 4 }, pb: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              bgcolor: 'white',
              borderRadius: '6px',
              '&:hover': { bgcolor: '#f0f0f0' }
            }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" fontWeight={600} color="#680000">
            Checkout
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Left Column - Customer Details */}
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: '6px', mb: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 3, color: '#680000' }}>
                Customer Information
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    name="name"
                    value={customerInfo.name}
                    onChange={handleInputChange}
                    placeholder="Enter customer name"
                    
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        '&:hover fieldset': { borderColor: '#680000' },
                        '&.Mui-focused fieldset': { borderColor: '#680000' },
                      },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#680000' },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <PhoneInput
                    country="sg"
                    specialLabel=""
                    // value={formData?.phoneNumber || ''}
                    onChange={(phone, country) => handlePhoneInputChange("phone", { target: { value: phone, countryValue: country } })}
                    inputStyle={{
                      width: '100%',
                      height: '50.25px',
                      borderRadius: '7px',
                      color: 'black',
                      paddingLeft: '58px',
                      fontSize: '12px !important',
                    }}
                    buttonStyle={{
                      borderTopLeftRadius: '7px',
                      borderBottomLeftRadius: '7px',
                    }}
                    containerStyle={{
                      width: '100%',
                    }}
                  />


                  {/* <TextField
                    fullWidth
                    label="Phone Number *"
                    name="phone"
                    value={customerInfo.phone}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        '&:hover fieldset': { borderColor: '#680000' },
                        '&.Mui-focused fieldset': { borderColor: '#680000' },
                      },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#680000' },
                    }}
                  /> */}
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email (Optional)"
                    name="email"
                    type="email"
                    value={customerInfo.email}
                    onChange={handleInputChange}
                    placeholder="Enter email address"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        '&:hover fieldset': { borderColor: '#680000' },
                        '&.Mui-focused fieldset': { borderColor: '#680000' },
                      },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#680000' },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Special Instructions (Optional)"
                    name="specialInstructions"
                    value={customerInfo.specialInstructions}
                    onChange={handleInputChange}
                    placeholder="Any special requests for your order?"
                    multiline
                    rows={3}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        '&:hover fieldset': { borderColor: '#680000' },
                        '&.Mui-focused fieldset': { borderColor: '#680000' },
                      },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#680000' },
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Payment Methods */}
            {/* <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: '6px' }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 3, color: '#680000' }}>
                Payment Method
              </Typography>

              <RadioGroup value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                <Grid container spacing={1}>
                  {paymentMethods.map((method) => (
                    <Grid item xs={12} sm={6} key={method.value}>
                      <FormControlLabel
                        value={method.value}
                        control={
                          <Radio
                            sx={{
                              color: '#680000',
                              '&.Mui-checked': { color: '#680000' },
                            }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {getPaymentMethodIcon(method.icon)}
                            <Typography>{method.label}</Typography>
                          </Box>
                        }
                        sx={{
                          width: '100%',
                          m: 0,
                          p: 1.5,
                          borderRadius: '6px',
                          border: '1px solid',
                          borderColor: paymentMethod === method.value ? '#680000' : '#e0e0e0',
                          bgcolor: paymentMethod === method.value ? '#FFF5F5' : 'white',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: '#680000',
                            bgcolor: '#FFF5F5',
                          },
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </RadioGroup>

              {paymentMethod === 'counter' && (
                <Alert severity="info" sx={{ mt: 2, borderRadius: '6px' }}>
                  Please proceed to the counter for payment after placing your order.
                </Alert>
              )}

              {paymentMethod === 'paynow' && (
                <Alert severity="info" sx={{ mt: 2, borderRadius: '6px' }}>
                  Scan the QR code at the counter to complete your payment via PayNow.
                </Alert>
              )}
            </Paper> */}
          </Grid>

          {/* Right Column - Order Summary */}
          <Grid item xs={12} md={5}>
            <Paper sx={{
              position: 'sticky',
              top: 80,
              p: { xs: 2, md: 3 },
              borderRadius: '6px',
              display: 'flex',
              flexDirection: 'column',
              height: { xs: 'auto', md: 'calc(100vh - 120px)' }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ShoppingCartOutlined sx={{ color: '#680000' }} />
                <Typography variant="h6" fontWeight={600}>
                  Order Summary
                </Typography>
                <Chip
                  label={`${displayItems.length} item${displayItems.length !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{ bgcolor: '#680000', color: 'white', borderRadius: '6px' }}
                />
              </Box>

              {/* Order Type Indicator */}
              <Box sx={{
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                bgcolor: '#f5f5f5',
                borderRadius: '6px'
              }}>
                {currentOrderType === 'dinein' ? (
                  <Restaurant sx={{ color: '#1565c0', fontSize: 20 }} />
                ) : (
                  <Edit sx={{ color: '#e65100', fontSize: 20 }} />
                )}
                <Typography variant="body2" fontWeight={500}>
                  {currentOrderType === 'dinein' ? 'Dine In Order' : 'Takeaway Order'}
                </Typography>
              </Box>

              {/* Scrollable Items List - Fixed Height */}
              <Box sx={{
                flex: 1,
                overflow: 'auto',
                minHeight: 0,
                mb: 2,
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#680000',
                  borderRadius: '6px',
                },
              }}>
                {cartLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={30} sx={{ color: '#680000' }} />
                  </Box>
                ) : displayItems.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No items in cart
                    </Typography>
                  </Box>
                ) : (
                  displayItems.map((item, index) => (
                    <Card
                      key={item.id || index}
                      sx={{
                        mb: 1.5,
                        borderRadius: '6px',
                        boxShadow: 'none',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {item.name}
                              </Typography>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: item.is_veg ? '#228B22' : '#B22222',
                                }}
                              />
                            </Box>
                            {item.size && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Size: {item.size}
                              </Typography>
                            )}
                            {item.addons?.length > 0 && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                + {item.addons.map(a => a.name).join(', ')}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" display="block">
                              Quantity: {item.quantity}
                            </Typography>
                          </Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            S${(item.total_price).toFixed(2)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  ))
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Price Breakdown - Sticky at bottom */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Subtotal
                  </Typography>
                  <Typography variant="body2">
                    S${calculateSubtotal().toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Tax ({cartValues.tax || orderDetails?.tax || 9}%)
                  </Typography>
                  <Typography variant="body2">
                    S${calculateTax(calculateSubtotal()).toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Service Charge ({cartValues.service_charge || orderDetails?.tax || 9}%)
                  </Typography>
                  <Typography variant="body2">
                    S${cartValues.service_charge_value}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight={700}>
                    Total
                  </Typography>
                  <Typography variant="h5" fontWeight={800} sx={{ color: '#680000' }}>
                    S${calculateTotal().toFixed(2)}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleOrderStatusChange()}
                    sx={{
                      borderColor: '#680000',
                      color: '#680000',
                      py: 1.5,
                      borderRadius: '6px',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: '#520000',
                        bgcolor: '#FFF5F5',
                      },
                    }}
                  >
                    Back to Cart
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handlePlaceOrder}
                    disabled={loading || displayItems.length === 0}
                    sx={{
                      bgcolor: '#680000',
                      py: 1.5,
                      borderRadius: '6px',
                      fontWeight: 600,
                      '&:hover': { bgcolor: '#520000' },
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Place Order'}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Confirm Order Dialog */}
      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        PaperProps={{
          sx: { borderRadius: '6px' }
        }}
      >
        <DialogTitle sx={{ color: '#680000', fontWeight: 600 }}>Confirm Order</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Please review your order details before confirming.
          </DialogContentText>
          <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: '6px', mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Customer:</strong> {customerInfo.name}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Phone:</strong> {customerInfo.phone === "" ? "" : `+${customerInfo.phone}`}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Payment Method:</strong> {paymentMethods.find(m => m.value === paymentMethod)?.label}
            </Typography>
            <Typography variant="body2">
              <strong>Total Amount:</strong> S${calculateTotal().toFixed(2)}
            </Typography>
          </Box>
          <DialogContentText variant="body2" color="text.secondary">
            By confirming, you agree to our terms and conditions.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setOpenConfirmDialog(false)} sx={{ color: '#680000' }}>
            Cancel
          </Button>
          <Button
            onClick={confirmOrder}
            variant="contained"
            sx={{ bgcolor: '#680000', borderRadius: '6px', '&:hover': { bgcolor: '#520000' } }}
          >
            Confirm Order
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TableOrderCheckout;